import { ChevronDown } from '@/components/icons/barrel';
import TooltipWrapper from '@/components/ui/tooltip';
import {
  formatWeekendCountdown,
  getWeekendCountdownTickMs,
} from '@/config/weekend-trading';
import useSymbol from '@/hooks/use-symbol';
import { useWeekendTrading } from '@/hooks/use-weekend-trading';
import { useAccountStore } from '@/store/use-account-store';
import {
  FUNDING_RATE_DECIMALS,
  useMarketStore,
} from '@/store/use-markets-store';
import { cn } from '@/utils/cn';
import { bigIntToNum, formatBigInt, formatNumber } from '@/utils/value-format';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export default function MarketInfo() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={dropdownRef} className="w-full">
      <button
        className="flex h-full w-full items-center justify-center gap-2 px-4 font-mono sm:gap-4 sm:px-6 xl:hidden"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="font-bold">
          <MidPrice />
        </span>
        <PriceChange className="text-sm" percentsOnly />
        <ChevronDown
          size={16}
          className={cn(
            'shrink-0 text-vestgrey-100 transition-transform',
            isOpen ? 'rotate-180' : 'rotate-0',
          )}
        />
      </button>
      <div
        className={cn(
          'absolute inset-x-0 top-full z-40 grid h-max w-full max-w-6xl grid-cols-2 items-center justify-between gap-4 border-y border-border bg-background px-6 py-4 transition-opacity sm:grid-cols-3 sm:px-8 xl:static xl:flex xl:h-full xl:gap-0 xl:border-y-0 xl:py-0',
          isOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0 xl:pointer-events-auto xl:opacity-100',
        )}
      >
        <div className="flex flex-col items-center gap-1 lg:items-start lg:gap-0.5">
          <h4 className="max-w-max select-none font-mono text-sm uppercase text-vestgrey-600">
            {t('marketInfo.midPrice')}
          </h4>
          <p className="text-xl font-bold text-vestgrey-50">
            <MidPrice />
          </p>
        </div>
        <div className="flex flex-col items-center gap-1 lg:items-start lg:gap-0.5">
          <TooltipWrapper content={t('marketInfo.indexPriceTooltip')}>
            <h4 className="max-w-max select-none font-mono text-sm uppercase text-vestgrey-600 underline">
              {t('marketInfo.indexPrice')}
            </h4>
          </TooltipWrapper>
          <div className="text-xl font-bold text-vestgrey-50">
            <IndexPrice />
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 lg:items-start lg:gap-0.5">
          <h4 className="font-mono text-sm uppercase text-vestgrey-600">
            {t('marketInfo.change24h')}
          </h4>
          <PriceChange className="text-xl font-bold" />
        </div>
        <Funding />
        <div className="flex flex-col items-center gap-1 lg:items-start lg:gap-0.5">
          <h4 className="font-mono text-sm uppercase text-vestgrey-600">
            {t('marketInfo.openInterest')}
          </h4>
          <div className="flex flex-1 items-center justify-start text-xl font-bold text-vestgrey-50">
            <OpenInterest />
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 lg:items-start lg:gap-0.5">
          <h4 className="font-mono text-sm uppercase text-vestgrey-600">
            {t('marketInfo.volume24h')}
          </h4>
          <div className="flex flex-1 items-center justify-start text-xl font-bold text-vestgrey-50">
            <QuoteVolume />
          </div>
        </div>
        <WeekendPauseCountdown />
      </div>
    </div>
  );
}

/**
 * "Market closes/opens in" column for the markets that observe the weekend
 * trading pause. Hidden entirely for crypto, which trades 24/7.
 */
function WeekendPauseCountdown() {
  const { t } = useTranslation();
  const symbol = useSymbol();
  const { isRestricted, isBlocked, changesAt, reason, reopenLabel } =
    useWeekendTrading(symbol);

  const timeOffset = useAccountStore((state) => state.timeOffset);
  const [now, setNow] = useState(
    () => new Date(useAccountStore.getState().getAccurateTime()),
  );

  useEffect(() => {
    if (!isRestricted) return;

    let timeout: ReturnType<typeof setTimeout>;

    // Self-rescheduling rather than a fixed interval: the cadence has to
    // tighten to 1s once the countdown renders seconds, so the displayed value
    // never lags the actual reopen.
    const tick = () => {
      clearTimeout(timeout);
      const nowMs = useAccountStore.getState().getAccurateTime();
      setNow(new Date(nowMs));
      timeout = setTimeout(
        tick,
        getWeekendCountdownTickMs(changesAt.getTime() - nowMs),
      );
    };

    const onVisibilityChange = () => {
      if (!document.hidden) tick();
    };

    tick();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isRestricted, changesAt, timeOffset]);

  if (!isRestricted) return null;

  return (
    <div className="flex flex-col items-center gap-1 lg:items-start lg:gap-0.5">
      <TooltipWrapper
        content={
          reason === 'funded'
            ? t('marketInfo.fundedClosedTooltip', { reopen: reopenLabel })
            : isBlocked
              ? t('marketInfo.marketOpensInTooltip', { reopen: reopenLabel })
              : t('marketInfo.marketClosesInTooltip', { reopen: reopenLabel })
        }
      >
        <h4 className="max-w-max select-none font-mono text-sm uppercase text-vestgrey-600 underline">
          {isBlocked
            ? t('marketInfo.marketOpensIn')
            : t('marketInfo.marketClosesIn')}
        </h4>
      </TooltipWrapper>
      <p className="text-xl font-bold text-vestgrey-50">
        {formatWeekendCountdown(changesAt.getTime() - now.getTime())}
      </p>
    </div>
  );
}

function MidPrice() {
  const symbol = useSymbol();
  const { midPrice, priceDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      midPrice: state.marketData.midPrice,
      priceDecimals: state.marketSpec.priceDecimals,
    })),
  );
  return formatBigInt(midPrice, priceDecimals, {
    digits: Number(priceDecimals),
    style: 'currency',
  });
}

function IndexPrice() {
  const symbol = useSymbol();
  const { indexPrice, priceDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      indexPrice: state.marketData.indexPrice,
      priceDecimals: state.marketSpec.priceDecimals,
    })),
  );
  return formatBigInt(indexPrice, priceDecimals, {
    digits: Number(priceDecimals),
    style: 'currency',
  });
}

function PriceChange({
  percentsOnly,
  className,
}: {
  percentsOnly?: boolean;
  className?: string;
}) {
  const symbol = useSymbol();
  const { priceChange, priceChangePct, priceDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      priceChange: state.marketData.priceChange,
      priceChangePct: state.marketData.priceChangePct,
      priceDecimals: state.marketSpec.priceDecimals,
    })),
  );
  return (
    <p
      className={cn(className, {
        'text-vestgrey-100': priceChange === 0n,
        'text-green': priceChange > 0n,
        'text-red': priceChange < 0n,
      })}
    >
      {formatBigInt(priceChangePct, 4, {
        showChange: true,
        style: 'percent',
      })}
      {!percentsOnly && (
        <>
          {' '}
          /{' '}
          {formatBigInt(priceChange, priceDecimals, {
            abs: true,
          })}
        </>
      )}
    </p>
  );
}

function Funding() {
  const symbol = useSymbol();
  const { t } = useTranslation();
  const eightHrFundingRate = useStore(useMarketStore(symbol), (state) =>
    bigIntToNum(
      state.marketData.oneHrFundingRate * 100n * 8n,
      FUNDING_RATE_DECIMALS,
    ),
  );
  return (
    <div className="flex flex-col items-center gap-1 lg:items-start lg:gap-0.5">
      <TooltipWrapper
        content={t('marketInfo.fundingTooltip', {
          fundingAPY: formatNumber(eightHrFundingRate * 3 * 365, {
            digits: 2,
            style: 'percent',
          }),
        })}
      >
        <h4 className="max-w-max select-none font-mono text-sm uppercase text-vestgrey-600 underline">
          {t('marketInfo.funding')}
        </h4>
      </TooltipWrapper>
      <div
        className={cn(
          'flex flex-1 items-center justify-start text-xl font-bold text-vestgrey-50',
          eightHrFundingRate >= 0 ? 'text-green' : 'text-red',
        )}
      >
        {formatNumber(eightHrFundingRate, {
          style: 'percent',
          digits: 4,
        })}
      </div>
    </div>
  );
}

function OpenInterest() {
  const symbol = useSymbol();
  const { openInterest, priceDecimals, sizeDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      openInterest: state.marketData.openInterest ?? 0n,
      priceDecimals: state.marketSpec.priceDecimals,
      sizeDecimals: state.marketSpec.sizeDecimals,
    })),
  );
  return formatBigInt(openInterest, priceDecimals + sizeDecimals, {
    style: 'currency',
    digits: 2,
    round: 'ceil',
    compact: true,
  });
}

function QuoteVolume() {
  const symbol = useSymbol();
  const { quoteVolume, priceDecimals, sizeDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      quoteVolume: state.marketData.quoteVolume ?? 0n,
      priceDecimals: state.marketSpec.priceDecimals,
      sizeDecimals: state.marketSpec.sizeDecimals,
    })),
  );
  return formatBigInt(quoteVolume, priceDecimals + sizeDecimals, {
    style: 'currency',
    digits: 2,
    round: 'ceil',
    compact: true,
  });
}

{
  /* <span
          className={cn(
            'text-sm',
            Number(data.priceChange) >= 0n ? 'text-green' : 'text-red',
          )}
        >
          <span className="hidden sm:inline">
            {formatNumber(data.priceChange, {
              digits: Number(marketSpec.priceDecimals),
              showChange: true,
            })}{' '}
            /{' '}
          </span>
          <span className="sm:hidden">
            {Number(data.priceChange) >= 0n ? '+' : '-'}
          </span>
          {formatNumber(data.priceChangePct, {
            style: 'percent',
            digits: 2,
            abs: true,
          })}
        </span> */
}
